/**
 * Event List View Container
 *
 * Main page for viewing calendar events in a list format.
 * Matches dark navy design system from dashboard.
 */

import React, { useState, useMemo } from 'react';
import { Plus, ChevronDown, Filter, Download, Settings, Info, Search, X } from 'lucide-react';
import { ListViewTable } from './ListViewTable';
import { Pagination } from './Pagination';
import { EditViewModal } from './EditViewModal';
import { EventModal } from '../modals/EventModal';
import {
  useEventListViews,
  useEventListViewData,
  useDefaultEventListView,
  useUpdateEventListView
} from '../../services/queries/eventListViewQueries';
import type { ListViewRecord, ListView } from '../../types/listView';
import { useToast } from '../../contexts/ToastContext';
import { useListViewColumns } from '../../hooks/useObjectManager';

interface EventListViewProps {
  onNavigateToEvent: (eventId: string) => void;
  onCreateEvent?: () => void;
}

export const EventListView: React.FC<EventListViewProps> = ({
  onNavigateToEvent,
  onCreateEvent
}) => {
  // Toast notifications
  const { success, info } = useToast();

  // Get default list view
  const { data: defaultView } = useDefaultEventListView();

  // State for selected list view
  const [selectedViewId, setSelectedViewId] = useState<string>(
    defaultView?.id || 'lv_events_all'
  );

  // State for view switcher dropdown
  const [isViewDropdownOpen, setIsViewDropdownOpen] = useState(false);

  // State for search
  const [searchQuery, setSearchQuery] = useState('');

  // State for sorting
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  // State for pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // State for edit view modal
  const [isEditViewModalOpen, setIsEditViewModalOpen] = useState(false);

  // State for event modal
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);

  // Fetch available list views
  const { data: listViews, isLoading: loadingViews } = useEventListViews();

  // Fetch data for selected list view
  const { data: listViewData, isLoading: loadingData, isFetching, isPlaceholderData } = useEventListViewData(selectedViewId, {
    page: currentPage - 1,
    pageSize,
  });
  const isFetchingPage = isFetching && isPlaceholderData;

  // Mutation for updating list view
  const updateListView = useUpdateEventListView();

  // Fetch dynamic columns from Object Manager
  const { data: dynamicColumns = [], isLoading: loadingColumns } = useListViewColumns('Event');

  // Merge dynamic columns into list view data
  const mergedListViewData = useMemo(() => {
    if (!listViewData) return null;
    const columnsToUse = dynamicColumns.length > 0 ? dynamicColumns : listViewData.listView.columns;
    const columnsWithVisibility = columnsToUse.map((col, index) => ({
      ...col,
      isVisible: index < 10
    }));
    return {
      ...listViewData,
      listView: {
        ...listViewData.listView,
        columns: columnsWithVisibility
      }
    };
  }, [listViewData, dynamicColumns]);

  // Update selected view when default view loads
  React.useEffect(() => {
    if (defaultView && !selectedViewId) {
      setSelectedViewId(defaultView.id);
    }
  }, [defaultView, selectedViewId]);

  // Handle record click
  const handleRecordClick = (record: ListViewRecord) => {
    onNavigateToEvent(record.id);
  };

  // Handle view change
  const handleViewChange = (viewId: string) => {
    setSelectedViewId(viewId);
    setIsViewDropdownOpen(false);
  };

  // Get current view name
  const currentViewName = listViews?.find(v => v.id === selectedViewId)?.name || 'All Events';

  // Filter records based on search
  const filteredRecords = React.useMemo(() => {
    if (!mergedListViewData?.records) return [];
    if (!searchQuery) return mergedListViewData.records;

    const query = searchQuery.toLowerCase();
    const visibleColumns = mergedListViewData.listView.columns.filter(col => col.isVisible);

    return mergedListViewData.records.filter(record => {
      // Search across all visible columns
      return visibleColumns.some(col => {
        const value = String(record[col.fieldApiName] || '').toLowerCase();
        return value.includes(query);
      });
    });
  }, [mergedListViewData, searchQuery]);

  // Apply sorting to filtered records
  const sortedRecords = React.useMemo(() => {
    if (!sortConfig) return filteredRecords;

    return [...filteredRecords].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue === bValue) return 0;

      const comparison = aValue < bValue ? -1 : 1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  }, [filteredRecords, sortConfig]);

  // Calculate pagination
  const totalRecords = listViewData?.totalRecords ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
  const recordsToShow = sortedRecords;

  // Reset to page 1 when search or filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedViewId]);

  // Handle CSV export
  const handleExport = () => {
    if (!mergedListViewData) return;

    const currentView = mergedListViewData.listView;

    // Get visible columns
    const columns = currentView.columns
      .filter(col => col.isVisible)
      .sort((a, b) => a.order - b.order);

    // Create CSV header
    const header = columns.map(col => col.fieldLabel).join(',');

    // Create CSV rows
    const rows = sortedRecords.map(record => {
      return columns.map(col => {
        const value = record[col.fieldApiName];
        // Escape commas and quotes
        const formatted = String(value || '').replace(/"/g, '""');
        return `"${formatted}"`;
      }).join(',');
    });

    // Combine header and rows
    const csv = [header, ...rows].join('\n');

    // Create download
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Events_${currentView.name}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);

    // Show success toast
    success('Export successful', `Downloaded ${sortedRecords.length} records (current page)`);
  };

  // Handle new event
  const handleNewEvent = () => {
    setIsEventModalOpen(true);
  };

  // Handle save list view
  const handleSaveListView = (updates: Partial<ListView>) => {
    if (!mergedListViewData) return;

    updateListView.mutate(
      { id: mergedListViewData.listView.id, updates },
      {
        onSuccess: () => {
          setIsEditViewModalOpen(false);
          success('View updated', 'List view settings have been saved');
        },
        onError: (error) => {
          console.error('Failed to update list view:', error);
        }
      }
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0f172a] p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Calendar & Events</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage meetings, calls, and scheduled activities
            </p>
          </div>
          <button
            onClick={handleNewEvent}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            New Event
          </button>
        </div>

      </div>

      {/* Controls Row */}
      <div className="mb-6 flex items-center gap-4">
        {/* Left: View Switcher */}
        <div className="relative">
          <button
            onClick={() => setIsViewDropdownOpen(!isViewDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-900 dark:text-white border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            disabled={loadingViews}
          >
            <span className="font-medium">{currentViewName}</span>
            <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          </button>

          {/* View Dropdown */}
          {isViewDropdownOpen && listViews && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-10"
                onClick={() => setIsViewDropdownOpen(false)}
              />

              {/* Dropdown Menu */}
              <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-[#1e293b] border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-20">
                <div className="py-2">
                  <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    List Views
                  </div>
                  {listViews.map((view) => (
                    <button
                      key={view.id}
                      onClick={() => handleViewChange(view.id)}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-[#334155] transition-colors ${
                        view.id === selectedViewId
                          ? 'bg-gray-100 dark:bg-[#334155] text-gray-900 dark:text-white font-medium'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{view.name}</span>
                        {view.isDefault && (
                          <span className="text-xs text-gray-500 dark:text-gray-500">(Default)</span>
                        )}
                      </div>
                      {view.description && (
                        <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">{view.description}</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Search Bar */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search events..."
            className="w-full pl-10 pr-10 py-2 bg-white dark:bg-[#1e293b] border border-gray-300 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Right: Action Buttons */}
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Filter records"
          >
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Filter</span>
          </button>

          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Export to CSV"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>

          <button
            onClick={() => setIsEditViewModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1e293b] hover:bg-gray-50 dark:hover:bg-[#334155] text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-700 rounded-lg transition-colors"
            title="Edit list view"
          >
            <Settings className="w-4 h-4" />
            <span className="hidden sm:inline">Edit View</span>
          </button>
        </div>
      </div>

      {/* Table */}
      {mergedListViewData && (
        <div className="relative">
          <ListViewTable
            listView={mergedListViewData.listView}
            records={recordsToShow}
            onRecordClick={handleRecordClick}
            isLoading={loadingData || loadingColumns}
            sortConfig={sortConfig}
            onSort={(field: string) => {
              setSortConfig(prev => {
                if (!prev || prev.field !== field) {
                  return { field, direction: 'asc' };
                }
                if (prev.direction === 'asc') {
                  return { field, direction: 'desc' };
                }
                return null;
              });
            }}
          />
          {isFetchingPage && (
            <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-white/70 dark:bg-slate-900/70 z-10" aria-busy="true" aria-label="Loading page">
              <div className="flex flex-col items-center gap-2 px-4 py-3 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-600 shadow-sm">
                <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Loading page {currentPage}...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {(listViewData?.totalRecords ?? 0) > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalRecords={totalRecords}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      )}

      {/* Loading State - Initial Load */}
      {!mergedListViewData && loadingData && (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading events...</p>
          </div>
        </div>
      )}

      {/* Edit View Modal */}
      {isEditViewModalOpen && mergedListViewData && (
        <EditViewModal
          listView={mergedListViewData.listView}
          onSave={handleSaveListView}
          onClose={() => setIsEditViewModalOpen(false)}
        />
      )}

      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={() => setIsEventModalOpen(false)}
      />
    </div>
  );
};
